export interface InstructorBrandingTerms {
  school: string;
  schoolEnglish: string;
  schoolNative: string;
  classContextIncomplete: string;
  schoolRequired: string;
}

type SupportedLanguageCode = 'en' | 'ar' | 'fr' | 'de';

const DEFAULT_TERMS: Record<SupportedLanguageCode, InstructorBrandingTerms> = {
  en: {
    school: 'School',
    schoolEnglish: 'School (English)',
    schoolNative: 'School (Native)',
    classContextIncomplete: 'Class context is incomplete. Missing school/program/stage data.',
    schoolRequired: 'School is required',
  },
  ar: {
    school: 'المدرسة',
    schoolEnglish: 'المدرسة (بالإنجليزية)',
    schoolNative: 'المدرسة (باللغة الأم)',
    classContextIncomplete: 'سياق الفصل غير مكتمل. بيانات المدرسة/البرنامج/المرحلة مفقودة.',
    schoolRequired: 'المدرسة مطلوبة',
  },
  fr: {
    school: 'Ecole',
    schoolEnglish: 'Ecole (anglais)',
    schoolNative: 'Ecole (langue maternelle)',
    classContextIncomplete: 'Le contexte de la classe est incomplet. Donnees ecole/programme/niveau manquantes.',
    schoolRequired: 'L ecole est requise',
  },
  de: {
    school: 'Schule',
    schoolEnglish: 'Schule (Englisch)',
    schoolNative: 'Schule (Muttersprache)',
    classContextIncomplete: 'Klassenkontext ist unvollstandig. Schul-/Programm-/Stufendaten fehlen.',
    schoolRequired: 'Schule ist erforderlich',
  },
};

const CORPORATE_TERMS: Record<SupportedLanguageCode, InstructorBrandingTerms> = {
  en: {
    school: 'Learning Center',
    schoolEnglish: 'Learning Center (English)',
    schoolNative: 'Learning Center (Native)',
    classContextIncomplete: 'Class context is incomplete. Missing learning center/program/stage data.',
    schoolRequired: 'Learning Center is required',
  },
  ar: {
    school: 'مركز التعلم',
    schoolEnglish: 'مركز التعلم (بالإنجليزية)',
    schoolNative: 'مركز التعلم (باللغة الأم)',
    classContextIncomplete: 'سياق الفصل غير مكتمل. بيانات مركز التعلم/البرنامج/المرحلة مفقودة.',
    schoolRequired: 'مركز التعلم مطلوب',
  },
  fr: {
    school: "Centre d'apprentissage",
    schoolEnglish: "Centre d'apprentissage (anglais)",
    schoolNative: "Centre d'apprentissage (langue maternelle)",
    classContextIncomplete: "Le contexte de la classe est incomplet. Donnees centre d'apprentissage/programme/niveau manquantes.",
    schoolRequired: "Le centre d'apprentissage est requis",
  },
  de: {
    school: 'Lernzentrum',
    schoolEnglish: 'Lernzentrum (Englisch)',
    schoolNative: 'Lernzentrum (Muttersprache)',
    classContextIncomplete: 'Klassenkontext ist unvollstandig. Lernzentrum-/Programm-/Stufendaten fehlen.',
    schoolRequired: 'Lernzentrum ist erforderlich',
  },
};

function normalizeLanguageCode(languageCode?: string | null): SupportedLanguageCode {
  const normalized = String(languageCode ?? 'en').trim().toLowerCase();

  if (normalized.startsWith('ar')) {
    return 'ar';
  }
  if (normalized.startsWith('fr')) {
    return 'fr';
  }
  if (normalized.startsWith('de')) {
    return 'de';
  }

  return 'en';
}

let runtimeCorporateMode = false;

export function setInstructorCorporateMode(enabled: boolean): void {
  runtimeCorporateMode = enabled;
}

export function isCorporateModeEnabled(_env: any): boolean {
  return runtimeCorporateMode;
}

export function getInstructorBrandingTerms(env: any, languageCode?: string | null): InstructorBrandingTerms {
  const lang = normalizeLanguageCode(languageCode);
  return isCorporateModeEnabled(env) ? CORPORATE_TERMS[lang] : DEFAULT_TERMS[lang];
}

export function buildInstructorBrandingTranslationOverrides(
  env: any,
  languageCode?: string | null,
): Record<string, string> {
  if (!isCorporateModeEnabled(env)) {
    return {};
  }

  const terms = getInstructorBrandingTerms(env, languageCode);

  return {
    School: terms.school,
    'School (English)': terms.schoolEnglish,
    'School (Native)': terms.schoolNative,
    'Class context is incomplete. Missing school/program/stage data.': terms.classContextIncomplete,
    'School is required': terms.schoolRequired,
  };
}
